import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    BarChart3,
    Trophy,
    TrendingDown,
    Target,
    PieChart as PieChartIcon,
    Tag,
    Sparkles,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    CartesianGrid,
} from "recharts";
import { Layout } from "@/components/Layout";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { analiticasTour } from "@/tours";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getAnalyticsOverview, autoDetectAllOutcomes } from "@/lib/api";
import { fmtEUR } from "@/lib/format";

const OUTCOME_LABEL = {
    won: "Ganadas",
    lost: "Perdidas",
    desierta: "Desiertas",
    pending: "Pendientes",
    withdrawn: "Retiradas",
};
const COLORS = {
    won: "#059669",
    lost: "#DC2626",
    desierta: "#64748B",
    pending: "#0055ff",
    withdrawn: "#94A3B8",
};

export default function Analytics() {
    const tour = useTour("tour:analytics", analiticasTour, { autoStart: true });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detecting, setDetecting] = useState(false);

    const reload = () => {
        setLoading(true);
        getAnalyticsOverview()
            .then(setData)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        reload();
    }, []);

    const onDetect = async () => {
        setDetecting(true);
        try {
            const res = await autoDetectAllOutcomes();
            if (res.updated === 0) {
                toast.info(
                    res.checked === 0
                        ? "No tienes licitaciones pendientes"
                        : `Comprobadas ${res.checked} licitaciones. Ninguna tiene aún resultado en PLCSP.`
                );
            } else {
                const wonCount = (res.matches || []).filter(
                    (m) => m.detected_status === "won"
                ).length;
                const lostCount = (res.matches || []).filter(
                    (m) => m.detected_status === "lost"
                ).length;
                toast.success(
                    `Detectadas ${res.updated} resoluciones: ${wonCount} ganadas, ${lostCount} perdidas`
                );
                reload();
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo detectar el resultado"
            );
        } finally {
            setDetecting(false);
        }
    };

    const pieData = useMemo(() => {
        if (!data) return [];
        return Object.entries(data.counts || {})
            .filter(([k]) => k !== "total")
            .map(([k, v]) => ({ name: OUTCOME_LABEL[k] || k, key: k, value: v }))
            .filter((x) => x.value > 0);
    }, [data]);

    const winRatePct = data ? Math.round(data.win_rate * 100) : 0;

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-8" data-testid="analytics-page">
                <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="overline mb-2 flex items-center gap-1.5">
                            <BarChart3 size={12} /> Analíticas
                        </div>
                        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium">
                            Seguimiento y benchmarking
                        </h1>
                        <p className="text-sm md:text-base text-slate-500 mt-2 max-w-2xl">
                            Monitoriza tu tasa de éxito, importes adjudicados y rendimiento por
                            familia CPV a partir de tu histórico de licitaciones.
                        </p>
                    </div>
                    <Button
                        onClick={onDetect}
                        disabled={detecting}
                        className="rounded-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-11 px-5 font-bold shadow-pop gap-2 disabled:opacity-60"
                        data-testid="analytics-autodetect-btn"
                        title="Cruza tus licitaciones pendientes con el histórico PLCSP y autocompleta el resultado"
                    >
                        {detecting ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Detectando…
                            </>
                        ) : (
                            <>
                                <Sparkles size={14} />
                                Detectar resultados pendientes
                            </>
                        )}
                    </Button>
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-4 gap-4">
                        {["s1", "s2", "s3", "s4"].map((k) => (
                            <Skeleton key={k} className="h-28" />
                        ))}
                    </div>
                ) : !data || data.counts.total === 0 ? (
                    <EmptyAnalytics />
                ) : (
                    <>
                        {/* KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-slate-200 bg-white mb-6">
                            <Kpi
                                label="Tasa de éxito"
                                value={`${winRatePct}%`}
                                icon={Trophy}
                                hint={`${data.counts.won || 0} de ${data.decided} decididas`}
                                borderRight
                            />
                            <Kpi
                                label="Adjudicado total"
                                value={fmtEUR(data.total_awarded)}
                                icon={Target}
                                mono
                                borderRight
                            />
                            <Kpi
                                label="Licitaciones"
                                value={data.counts.total}
                                icon={PieChartIcon}
                                hint={`${data.counts.pending || 0} pendientes`}
                                borderRight
                            />
                            <Kpi
                                label="Baja media en ganadas"
                                value={`${Math.round(data.avg_win_discount * 100)}%`}
                                icon={TrendingDown}
                                hint="sobre presupuesto base"
                            />
                        </div>

                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                            {/* Pie */}
                            <div className="bg-white border border-slate-200 p-6">
                                <div className="overline mb-4 flex items-center gap-1.5">
                                    <PieChartIcon size={12} /> Resultados
                                </div>
                                <div className="h-64">
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={95}
                                                paddingAngle={2}
                                            >
                                                {pieData.map((e) => (
                                                    <Cell
                                                        key={e.key}
                                                        fill={COLORS[e.key] || "#94A3B8"}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{
                                                    borderRadius: 2,
                                                    border: "1px solid #E2E8F0",
                                                    fontSize: 13,
                                                }}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Bar by CPV */}
                            <div className="bg-white border border-slate-200 p-6">
                                <div className="overline mb-4 flex items-center gap-1.5">
                                    <Tag size={12} /> Actividad por CPV (top 8)
                                </div>
                                <div className="h-64">
                                    {data.by_cpv.length === 0 ? (
                                        <div className="h-full flex items-center justify-center text-sm text-slate-400">
                                            Aún no hay CPV registrados en tus licitaciones
                                        </div>
                                    ) : (
                                        <ResponsiveContainer>
                                            <BarChart data={data.by_cpv.slice(0, 8)}>
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    stroke="#E2E8F0"
                                                />
                                                <XAxis
                                                    dataKey="cpv"
                                                    tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                                                />
                                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                                <Tooltip
                                                    contentStyle={{
                                                        borderRadius: 2,
                                                        border: "1px solid #E2E8F0",
                                                        fontSize: 12,
                                                    }}
                                                />
                                                <Bar dataKey="count" fill="#0055ff" name="Total" />
                                                <Bar dataKey="won" fill="#059669" name="Ganadas" />
                                                <Bar dataKey="lost" fill="#DC2626" name="Perdidas" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* CPV table */}
                        <div className="bg-white border border-slate-200">
                            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                                <Tag size={14} className="text-[#0055ff]" />
                                <div className="font-display font-medium">Benchmarking por CPV</div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full data-table">
                                    <thead>
                                        <tr>
                                            <th>CPV</th>
                                            <th className="text-right">Total</th>
                                            <th className="text-right">Ganadas</th>
                                            <th className="text-right">Perdidas</th>
                                            <th className="text-right">Desiertas</th>
                                            <th className="text-right">Tasa éxito</th>
                                            <th className="text-right">Adjudicado medio</th>
                                            <th className="text-right">Presup. base total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.by_cpv.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={8}
                                                    className="text-center py-10 text-slate-400 text-sm"
                                                >
                                                    Aún no hay CPV en tu histórico
                                                </td>
                                            </tr>
                                        ) : (
                                            data.by_cpv.map((row) => (
                                                <tr key={row.cpv} data-testid={`cpv-row-${row.cpv}`}>
                                                    <td>
                                                        <span className="font-mono text-xs bg-slate-100 text-slate-800 px-2 py-0.5 rounded-xl">
                                                            {row.cpv}
                                                        </span>
                                                    </td>
                                                    <td className="num">{row.count}</td>
                                                    <td className="num text-emerald-700">
                                                        {row.won}
                                                    </td>
                                                    <td className="num text-red-700">{row.lost}</td>
                                                    <td className="num text-slate-500">
                                                        {row.desierta}
                                                    </td>
                                                    <td className="num font-semibold">
                                                        {Math.round((row.win_rate || 0) * 100)}%
                                                    </td>
                                                    <td className="num">
                                                        {fmtEUR(row.avg_awarded)}
                                                    </td>
                                                    <td className="num text-slate-500">
                                                        {fmtEUR(row.total_budget)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}

const Kpi = ({ label, value, icon: Icon, hint, mono, borderRight }) => (
    <div
        className={`p-6 ${borderRight ? "md:border-r border-slate-200" : ""} border-slate-200`}
    >
        <div className="flex items-center gap-2 overline mb-3">
            <Icon size={12} />
            {label}
        </div>
        <div
            className={`${mono ? "font-mono" : "font-display"} text-2xl md:text-3xl font-medium text-slate-950 tracking-tight`}
        >
            {value}
        </div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
);

const EmptyAnalytics = () => (
    <div className="bg-white border border-slate-200 py-16 text-center">
        <div className="w-12 h-12 mx-auto bg-[#eff5ff] text-[#0055ff] border border-sky-100 flex items-center justify-center rounded-xl mb-4">
            <BarChart3 size={20} />
        </div>
        <div className="font-display text-xl font-medium mb-2">Sin datos todavía</div>
        <div className="text-sm text-slate-500 max-w-md mx-auto mb-5">
            Crea y cierra licitaciones (ganadas, perdidas, desiertas) para ver aquí tus métricas y
            el benchmarking por CPV.
        </div>
        <Link
            to="/app/nueva"
            className="inline-flex items-center gap-1.5 bg-[#0055ff] text-white text-sm font-medium px-4 h-10 rounded-xl hover:bg-[#0044cc] transition-colors"
        >
            Nueva licitación
        </Link>
    </div>
);
